import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { GroupService } from '../../../services/group.service';
import { ProfileService } from '../../../services/profile.service';
import { Group } from '../../../dtos/group';
import { UserInfo } from '../../../dtos/user-info';
import { PaginatedResponse } from '../../../dtos/paginated';
import { GroupCreateModalComponent } from '../../modals/group-create-modal/group-create-modal.component';
import { GroupEditModalComponent } from '../../modals/group-edit-modal/group-edit-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-groups-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GroupCreateModalComponent,
    GroupEditModalComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './groups-overview.component.html',
  styleUrls: ['./groups-overview.component.scss'],
})
export class GroupsOverviewComponent implements OnInit, OnDestroy {
  private groupService = inject(GroupService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private location = inject(Location);

  readonly groupsPage = signal<PaginatedResponse<Group> | null>(null);
  readonly searchQuery = signal('');
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);
  readonly loading = signal(true);
  readonly errorMessage = signal('');
  private readonly search$ = new Subject<{ q: string; page: number }>();
  private userInfo: UserInfo | null = null;
  private sub?: Subscription;

  readonly groups = computed(() => this.groupsPage()?.items ?? []);
  readonly totalItems = computed(() => this.groupsPage()?.totalItems ?? 0);

  showCreateModal = false;
  showEditModal = false;
  selectedGroup: Group | null = null;
  copiedInviteGroupId: string | null = null;

  showLeaveConfirm = false;
  groupToLeave: Group | null = null;

  ngOnInit(): void {
    const searchSub = this.search$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.currentPage.set(page);
          return this.groupService.getGroupsPaginated(page, PAGE_SIZE, q || undefined);
        }),
      )
      .subscribe({
        next: (res) => {
          this.groupsPage.set(res);
          this.totalPages.set(res.totalPages);
          this.currentPage.set(res.currentPage);
          this.loading.set(false);
          this.errorMessage.set('');
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message ?? 'Could not load groups.');
          this.loading.set(false);
        },
      });

    this.sub = new Subscription();
    this.sub.add(searchSub);

    this.loadData();

    this.profileService.getCurrentUser().subscribe({
      next: (user) => {
        this.userInfo = user;
      },
      error: () => {},
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadData(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.groupService.getGroupsPaginated(0, PAGE_SIZE).subscribe({
      next: (res) => {
        this.groupsPage.set(res);
        this.totalPages.set(res.totalPages);
        this.currentPage.set(res.currentPage);
        this.loading.set(false);
      },
      error: (err) => {
        this.errorMessage.set(err?.error?.message ?? 'Could not load groups.');
        this.loading.set(false);
      },
    });
  }

  private triggerSearch(): void {
    this.search$.next({ q: this.searchQuery(), page: this.currentPage() });
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.currentPage.set(0);
    this.triggerSearch();
  }

  hasActiveFilters(): boolean {
    return this.searchQuery() !== '';
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.triggerSearch();
  }

  canPrev(): boolean {
    return this.currentPage() > 0;
  }

  canNext(): boolean {
    return this.currentPage() < this.totalPages() - 1;
  }

  isGroupOwner(group: Group): boolean {
    return !!this.userInfo?.id && String(group.ownerUserId) === String(this.userInfo.id);
  }

  buildInviteLink(group: Group): string {
    const tree = this.router.createUrlTree(['/join-group'], {
      queryParams: { inviteToken: group.inviteToken },
    });
    return `${window.location.origin}${this.location.prepareExternalUrl(this.router.serializeUrl(tree))}`;
  }

  async copyInviteLink(group: Group): Promise<void> {
    if (!group.inviteActive || !group.inviteToken) {
      return;
    }

    const inviteLink = this.buildInviteLink(group);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = inviteLink;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      this.copiedInviteGroupId = group.id;
      window.setTimeout(() => {
        if (this.copiedInviteGroupId === group.id) {
          this.copiedInviteGroupId = null;
        }
      }, 2000);
    } catch (error) {
      console.error('Could not copy invite link', error);
    }
  }

  viewGroupDetails(group: Group): void {
    this.router.navigate(['/groups', group.id]);
  }

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  onGroupCreated(): void {
    this.showCreateModal = false;
    this.currentPage.set(0);
    this.triggerSearch();
  }

  openEditModal(group: Group): void {
    if (!this.isGroupOwner(group)) {
      return;
    }
    this.selectedGroup = group;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedGroup = null;
  }

  saveGroup(updatedGroup: Group): void {
    this.closeEditModal();
    this.triggerSearch();
  }

  deleteGroup(groupId: string): void {
    this.closeEditModal();
    this.triggerSearch();
  }

  askLeaveGroup(group: Group): void {
    this.groupToLeave = group;
    this.showLeaveConfirm = true;
  }

  confirmLeaveGroup(): void {
    if (!this.groupToLeave) {
      return;
    }

    this.groupService.leaveGroup(this.groupToLeave.id).subscribe({
      next: () => {
        this.cancelLeaveGroup();
        this.triggerSearch();
      },
      error: (err) => {
        console.error('Could not leave group', err);
      },
    });
  }

  cancelLeaveGroup(): void {
    this.showLeaveConfirm = false;
    this.groupToLeave = null;
  }

  get leaveConfirmMessage(): string {
    return this.groupToLeave
      ? `Are you sure you want to leave the group "${this.groupToLeave.name}"? This action cannot be undone.`
      : '';
  }
}
